(function () {
  'use strict';

  var modelViewerPromise = null;
  var DEFAULT_CAMERA_ORBIT = '0deg 75deg auto';
  var DEFAULT_CAMERA_TARGET = 'auto auto auto';

  function ensureModelViewer() {
    if (window.customElements && window.customElements.get('model-viewer')) {
      return Promise.resolve();
    }

    if (!modelViewerPromise) {
      modelViewerPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    return modelViewerPromise;
  }

  function prefetchAsset(url) {
    if (!url) return;

    var selector = 'link[data-rabitat-prefetch="' + url + '"]';
    if (document.querySelector(selector)) return;

    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'fetch';
    link.href = url;
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-rabitat-prefetch', url);
    document.head.appendChild(link);
  }

  function screenPointFromTextStyle(textStyle, width, height) {
    var leftRaw = textStyle && textStyle.left != null ? String(textStyle.left) : '50%';
    var bottomRaw = textStyle && textStyle.bottom != null ? String(textStyle.bottom) : '20%';
    var x = width / 2;
    var y = height * 0.8;

    if (leftRaw.indexOf('%') !== -1) {
      x = (width * parseFloat(leftRaw)) / 100;
    } else if (!isNaN(parseFloat(leftRaw))) {
      x = parseFloat(leftRaw);
    }

    if (bottomRaw.indexOf('%') !== -1) {
      y = height * (1 - parseFloat(bottomRaw) / 100);
    } else if (!isNaN(parseFloat(bottomRaw))) {
      y = height - parseFloat(bottomRaw);
    }

    return { x: x, y: y };
  }

  function formatVector3D(vec) {
    if (vec == null) return null;
    if (typeof vec === 'string') return vec;

    function part(value) {
      var str = String(value == null ? 0 : value);
      return str.indexOf('m') === -1 ? str + 'm' : str;
    }

    return part(vec.x) + ' ' + part(vec.y) + ' ' + part(vec.z);
  }

  function waitForViewerReady(viewer) {
    return new Promise(function (resolve) {
      if (viewer && viewer.updateComplete && typeof viewer.updateComplete.then === 'function') {
        viewer.updateComplete.then(resolve).catch(resolve);
        return;
      }

      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    });
  }

  function prepareViewerCamera(viewer, config) {
    if (!viewer) return;

    var orbit = DEFAULT_CAMERA_ORBIT;
    var target = DEFAULT_CAMERA_TARGET;

    if (typeof config.getCameraOrbit === 'function') {
      orbit = config.getCameraOrbit() || orbit;
    }

    if (typeof config.getCameraTarget === 'function') {
      target = config.getCameraTarget() || target;
    }

    viewer.cameraOrbit = orbit;
    viewer.cameraTarget = target;

    if (typeof viewer.jumpCameraToGoal === 'function') {
      viewer.jumpCameraToGoal();
    }
  }

  function clearHotspots(viewer) {
    if (!viewer) return;
    viewer.querySelectorAll('[slot^="hotspot-"]').forEach(function (el) {
      el.remove();
    });
  }

  function applyHotspotLabel(label, point, config) {
    if (!label || !point) return;

    label.textContent = point.text || '';

    if (typeof config.applyHotspotLabel === 'function') {
      config.applyHotspotLabel(label, point.textStyle, point.slot);
    }
  }

  function createHotspot(viewer, index, anchor, point, config) {
    var hotspot = document.createElement('button');
    hotspot.type = 'button';
    hotspot.className = 'rabitat-inline-3d-hotspot';
    hotspot.setAttribute('slot', 'hotspot-' + index);
    hotspot.setAttribute('tabindex', '-1');
    hotspot.setAttribute('aria-hidden', 'true');

    if (anchor.type === 'surface') {
      hotspot.setAttribute('data-surface', anchor.surface);
    } else {
      hotspot.setAttribute('data-position', anchor.position);
      hotspot.setAttribute('data-normal', anchor.normal);
    }

    hotspot.dataset.hotspotSlot = String(point.slot == null ? index : point.slot);

    var label = document.createElement('div');
    label.className = 'rabitat-inline-3d-hotspot__label';
    applyHotspotLabel(label, point, config);
    hotspot.appendChild(label);
    viewer.appendChild(hotspot);
    return hotspot;
  }

  function getFallbackHotspot(config) {
    if (typeof config.getHotspotFallback === 'function') {
      return config.getHotspotFallback();
    }
    return null;
  }

  function resolveViewerPoint(viewer, point) {
    var width = viewer.clientWidth || viewer.getBoundingClientRect().width;
    var height = viewer.clientHeight || viewer.getBoundingClientRect().height;

    if (point.x != null && point.y != null) {
      return { x: point.x, y: point.y };
    }

    if (point.textStyle) {
      return screenPointFromTextStyle(point.textStyle, width, height);
    }

    return {
      x: width / 2,
      y: height * 0.8
    };
  }

  function resolveHotspotAnchor(viewer, point, config) {
    if (point.position && point.normal) {
      return {
        type: 'position',
        position: point.position,
        normal: point.normal
      };
    }

    if (point.surface) {
      return {
        type: 'surface',
        surface: point.surface
      };
    }

    var coords = resolveViewerPoint(viewer, point);
    var x = Math.max(2, Math.min((viewer.clientWidth || 0) - 2, coords.x));
    var y = Math.max(2, Math.min((viewer.clientHeight || 0) - 2, coords.y));

    if (typeof viewer.surfaceFromPoint === 'function') {
      var surface = viewer.surfaceFromPoint(x, y);
      if (surface) {
        return { type: 'surface', surface: surface };
      }
    }

    if (typeof viewer.positionAndNormalFromPoint === 'function') {
      var hit = viewer.positionAndNormalFromPoint(x, y);
      if (hit && hit.position && hit.normal) {
        return {
          type: 'position',
          position: formatVector3D(hit.position),
          normal: formatVector3D(hit.normal)
        };
      }
    }

    if (typeof config.resolveHotspotAnchor === 'function') {
      return config.resolveHotspotAnchor(viewer, point, coords);
    }

    return null;
  }

  function placeHotspots(viewer, points, config) {
    clearHotspots(viewer);
    if (!viewer || !points || !points.length) return Promise.resolve();

    prepareViewerCamera(viewer, config);

    return waitForViewerReady(viewer)
      .then(function () {
        if (typeof viewer.jumpCameraToGoal === 'function') {
          viewer.jumpCameraToGoal();
        }
        return waitForViewerReady(viewer);
      })
      .then(function () {
      var fallback = getFallbackHotspot(config);

      points.forEach(function (point, index) {
        var anchor = resolveHotspotAnchor(viewer, point, config);

        if (!anchor && fallback) {
          if (fallback.surface) {
            anchor = { type: 'surface', surface: fallback.surface };
          } else if (fallback.position && fallback.normal) {
            anchor = {
              type: 'position',
              position: fallback.position,
              normal: fallback.normal
            };
          }
        }

        if (anchor) {
          createHotspot(viewer, index + 1, anchor, point, config);
        }
      });
    });
  }

  function mapPointsToViewer(viewer, root, points) {
    if (!viewer || !root || !points || !points.length) return points || [];

    var viewerRect = viewer.getBoundingClientRect();
    var rootRect = root.getBoundingClientRect();

    if (
      Math.abs(viewerRect.width - rootRect.width) < 1 &&
      Math.abs(viewerRect.height - rootRect.height) < 1
    ) {
      return points;
    }

    var scaleX = viewerRect.width / (rootRect.width || 1);
    var scaleY = viewerRect.height / (rootRect.height || 1);
    var offsetX = viewerRect.left - rootRect.left;
    var offsetY = viewerRect.top - rootRect.top;

    return points.map(function (point) {
      if (point.x == null || point.y == null) return point;

      return {
        slot: point.slot,
        text: point.text,
        textStyle: point.textStyle,
        position: point.position,
        normal: point.normal,
        surface: point.surface,
        x: (point.x - offsetX) * scaleX,
        y: (point.y - offsetY) * scaleY
      };
    });
  }

  function updateHotspotLabels(viewer, points, config) {
    if (!viewer || !points || !points.length) return false;

    var hotspots = viewer.querySelectorAll('[slot^="hotspot-"]');
    if (!hotspots.length) return false;

    if (hotspots.length !== points.length) return false;

    hotspots.forEach(function (hotspot, index) {
      var label = hotspot.querySelector('.rabitat-inline-3d-hotspot__label');
      applyHotspotLabel(label, points[index], config);
    });

    return true;
  }

  function init(root, config) {
    if (!root || root.dataset.inline3dInit === 'true') {
      return root && root.__inline3dApi ? root.__inline3dApi : null;
    }

    root.dataset.inline3dInit = 'true';

    var btn = root.querySelector('[data-inline-3d-toggle]');
    var icon360 = root.querySelector('[data-inline-3d-icon-360]');
    var iconClose = root.querySelector('[data-inline-3d-icon-close]');
    var layer2d = root.querySelector('[data-inline-3d-2d]');
    var layer3d = root.querySelector('[data-inline-3d-3d]');
    var viewer = root.querySelector('[data-inline-3d-viewer]');
    var overlays3d = root.querySelector('[data-inline-3d-overlays]');
    var isOpen = false;
    var pendingLoadHandler = null;

    function getHotspotPoints() {
      if (typeof config.getHotspotPoints === 'function') {
        return config.getHotspotPoints();
      }
      return [];
    }

    function syncLegacyOverlays() {
      if (!overlays3d || typeof config.syncOverlays !== 'function') return;
      overlays3d.innerHTML = '';
      config.syncOverlays(overlays3d);
    }

    function syncHotspots(cachedPoints) {
      if (!viewer) return Promise.resolve();

      var points = cachedPoints || getHotspotPoints();
      points = mapPointsToViewer(viewer, root, points);

      if (typeof config.syncHotspots === 'function') {
        config.syncHotspots(viewer, points);
        return Promise.resolve();
      }

      if (points.length) {
        if (overlays3d) overlays3d.innerHTML = '';
        return placeHotspots(viewer, points, config);
      }

      syncLegacyOverlays();
      return Promise.resolve();
    }

    function resyncHotspots() {
      if (!isOpen || !viewer) return;

      var points = mapPointsToViewer(viewer, root, getHotspotPoints());

      if (!updateHotspotLabels(viewer, points, config)) {
        placeHotspots(viewer, points, config);
      }
    }

    function detachLoadHandler() {
      if (!viewer || !pendingLoadHandler) return;
      viewer.removeEventListener('load', pendingLoadHandler);
      pendingLoadHandler = null;
    }

    function lockRootHeight() {
      if (root.hasAttribute('data-pdp-hero-inline-3d-root') && root.offsetHeight > 0) {
        root.style.minHeight = root.offsetHeight + 'px';
      }
    }

    function unlockRootHeight() {
      if (root.hasAttribute('data-pdp-hero-inline-3d-root')) {
        root.style.minHeight = '';
      }
    }

    function close3d() {
      if (!isOpen) return;

      detachLoadHandler();

      if (layer3d) {
        layer3d.hidden = true;
        layer3d.setAttribute('hidden', '');
      }

      if (layer2d) {
        layer2d.hidden = false;
        layer2d.removeAttribute('hidden');
      }

      if (viewer) {
        clearHotspots(viewer);
        viewer.removeAttribute('src');
        viewer.removeAttribute('poster');
      }

      if (overlays3d) overlays3d.innerHTML = '';
      root.classList.remove('is-3d-active');
      isOpen = false;
      unlockRootHeight();

      if (btn) {
        btn.setAttribute('aria-label', 'View bottle in 360 degrees');
        btn.title = 'View bottle in 360°';
      }

      if (icon360) icon360.removeAttribute('aria-hidden');
      if (iconClose) iconClose.setAttribute('aria-hidden', 'true');
    }

    function reveal3dLayer() {
      lockRootHeight();

      if (layer2d) {
        layer2d.hidden = true;
        layer2d.setAttribute('hidden', '');
      }

      if (layer3d) {
        layer3d.hidden = false;
        layer3d.removeAttribute('hidden');
      }

      root.classList.add('is-3d-active');
      isOpen = true;

      if (btn) {
        btn.setAttribute('aria-label', 'Close 360 preview');
        btn.title = 'Close 360 preview';
      }

      if (icon360) icon360.setAttribute('aria-hidden', 'true');
      if (iconClose) iconClose.removeAttribute('aria-hidden');
    }

    function open3d() {
      if (typeof config.canOpen === 'function' && !config.canOpen()) return;

      var glbUrl = typeof config.getGlbUrl === 'function' ? config.getGlbUrl() : null;
      if (!glbUrl || !viewer) return;

      var hotspotPoints = getHotspotPoints();

      ensureModelViewer()
        .then(function () {
          detachLoadHandler();

          var poster = typeof config.getPosterUrl === 'function' ? config.getPosterUrl() : null;
          if (poster) {
            viewer.setAttribute('poster', poster);
          } else {
            viewer.removeAttribute('poster');
          }

          if (typeof config.getCameraOrbit === 'function') {
            viewer.setAttribute('camera-orbit', config.getCameraOrbit() || DEFAULT_CAMERA_ORBIT);
          } else {
            viewer.setAttribute('camera-orbit', DEFAULT_CAMERA_ORBIT);
          }

          function onModelReady() {
            syncHotspots(hotspotPoints);
          }

          pendingLoadHandler = function () {
            pendingLoadHandler = null;
            onModelReady();
          };

          reveal3dLayer();

          viewer.addEventListener('load', pendingLoadHandler);
          viewer.setAttribute('src', glbUrl);

          if (viewer.loaded) {
            onModelReady();
            detachLoadHandler();
          }
        })
        .catch(function () {
          if (typeof config.onError === 'function') config.onError();
        });
    }

    function toggle3d() {
      if (isOpen) {
        close3d();
      } else {
        open3d();
      }
    }

    var warmedUp = false;

    function warmupAssets() {
      if (warmedUp) return;
      if (typeof config.canOpen === 'function' && !config.canOpen()) return;

      warmedUp = true;
      ensureModelViewer().catch(function () {});

      var glbUrl = typeof config.getGlbUrl === 'function' ? config.getGlbUrl() : null;
      if (glbUrl) prefetchAsset(glbUrl);
    }

    var hydeCroPerf = document.documentElement.getAttribute('data-hyde-cro-perf') === '1';

    if (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        toggle3d();
      });

      if (!hydeCroPerf) {
        btn.addEventListener('pointerenter', warmupAssets, { once: true });
        btn.addEventListener('focus', warmupAssets, { once: true });
      }
    }

    if (!hydeCroPerf) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          function () {
            warmupAssets();
          },
          { timeout: 3500 }
        );
      } else {
        window.setTimeout(warmupAssets, 2000);
      }
    }

    var api = {
      open: open3d,
      close: close3d,
      toggle: toggle3d,
      resyncHotspots: resyncHotspots,
      isOpen: function () {
        return isOpen;
      }
    };

    root.__inline3dApi = api;
    return api;
  }

  window.RabitatInline3d = {
    init: init,
    ensureModelViewer: ensureModelViewer,
    prefetchAsset: prefetchAsset,
    screenPointFromTextStyle: screenPointFromTextStyle
  };
})();
